import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button, Form, List, Loader, Message, Modal, TextArea,
} from 'semantic-ui-react';

import {
  PENDING_VRA_NOTES_HEADER,
  PENDING_VRA_NOTES_EMPTY_MESSAGE,
  PENDING_VRA_NOTES_LOADING_MESSAGE,
  PENDING_VRA_NOTE_CANCEL_LABEL,
  PENDING_VRA_NOTE_CONFIRM_LABEL,
  PENDING_VRA_NOTE_DATE_DISPLAY_FORMAT,
  PENDING_VRA_NOTE_DATE_PARSE_FORMAT,
  PENDING_VRA_NOTE_FIELD_LABEL,
  PENDING_VRA_NOTE_MAX_LENGTH,
  PENDING_VRA_NOTES_SORT_LABEL,
  PENDING_VRA_NOTE_REQUIRED_MESSAGE,
} from '../../constants/pendingVraNotes';
import './PendingVraNotesDialog.css';

export const formatNoteDate = value => {
  const parsed = moment(value, PENDING_VRA_NOTE_DATE_PARSE_FORMAT);
  return value && parsed.isValid() ? parsed.format(PENDING_VRA_NOTE_DATE_DISPLAY_FORMAT) : '';
};

// APMS already answers newest first; sorting here keeps the dialog right even if a list arrives in another order.
export const newestFirst = notes => [...notes].sort((a, b) => {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;
  return Number(b.id) - Number(a.id);
});

const PendingVraNotesDialog = ({
  vraId,
  dealerName,
  notes,
  notesLoading,
  notesError,
  submitting,
  errorMessage,
  onClose,
  onConfirm,
  canAddNote,
}) => {
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState('');
  const scrollRef = useRef(null);

  const remaining = PENDING_VRA_NOTE_MAX_LENGTH - note.length;
  const loaded = !notesLoading && !notesError;

  // Newest note is listed first, so after the list changes (e.g. a note was just added) show the top again.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [notes]);

  const handleConfirm = async() => {
    if (submitting) return;
    if (!note.trim()) {
      setValidationError(PENDING_VRA_NOTE_REQUIRED_MESSAGE);
      return;
    }
    setValidationError('');
    const result = await onConfirm({ note: note.trim() });
    if (!result || result.success) setNote('');
  };

  return (
    <Modal
      open
      size='small'
      onClose={onClose}
      closeIcon
      closeOnDimmerClick={false}
      data-testid='pending-vra-notes-dialog'
    >
      <Modal.Header>{PENDING_VRA_NOTES_HEADER}</Modal.Header>
      <Modal.Content>
        <div className='pending-vra-notes-summary'>
          <span className='pending-vra-badge pending-vra-badge-id' data-testid='pending-vra-notes-vra'>
            {`VRA - ${vraId}`}
          </span>
          <span className='pending-vra-notes-dealer' data-testid='pending-vra-notes-dealer'>
            {dealerName || ''}
          </span>
        </div>

        {loaded && (
          <div className='pending-vra-notes-count' data-testid='pending-vra-notes-count'>
            {`Notes (${notes.length}) · ${PENDING_VRA_NOTES_SORT_LABEL}`}
          </div>
        )}

        {notesLoading && (
          <Loader active inline='centered' data-testid='pending-vra-notes-loading'>
            {PENDING_VRA_NOTES_LOADING_MESSAGE}
          </Loader>
        )}

        {!notesLoading && notesError && (
          <Message negative data-testid='pending-vra-notes-load-error'>
            <p>{notesError}</p>
          </Message>
        )}

        {loaded && notes.length === 0 && (
          <Message info className='pending-vra-notes-empty' data-testid='pending-vra-notes-empty'>
            <p>{PENDING_VRA_NOTES_EMPTY_MESSAGE}</p>
          </Message>
        )}

        {loaded && notes.length > 0 && (
          <div className='pending-vra-notes-scroll' ref={scrollRef} data-testid='pending-vra-notes-scroll'>
            <List data-testid='pending-vra-notes-list'>
              {newestFirst(notes).map(entry => (
                <List.Item
                  key={entry.id}
                  className='pending-vra-note'
                  data-testid='pending-vra-note-entry'
                >
                  <List.Content>
                    <div className='pending-vra-note-meta'>
                      <span data-testid='pending-vra-note-author'>{entry.createdBy}</span>
                      {' — '}
                      <span data-testid='pending-vra-note-timestamp'>{formatNoteDate(entry.createdAt)}</span>
                    </div>
                    <List.Description className='pending-vra-note-text' data-testid='pending-vra-note-text'>
                      {entry.note}
                    </List.Description>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          </div>
        )}

        {loaded && (
          <Form data-testid='pending-vra-notes-form'>
            <Form.Field required error={Boolean(validationError)}>
              <label htmlFor='pending-vra-note-input'>{PENDING_VRA_NOTE_FIELD_LABEL}</label>
              <TextArea
                id='pending-vra-note-input'
                value={note}
                disabled={submitting || !canAddNote}
                maxLength={PENDING_VRA_NOTE_MAX_LENGTH}
                data-testid='pending-vra-note-input'
                onChange={(event, { value }) => {
                  setNote(String(value).slice(0, PENDING_VRA_NOTE_MAX_LENGTH));
                  setValidationError('');
                }}
              />
            </Form.Field>
            <div className='pending-vra-note-remaining' data-testid='pending-vra-note-remaining'>
              {`${remaining} of ${PENDING_VRA_NOTE_MAX_LENGTH} characters remaining`}
            </div>
            {!canAddNote && (
              <Message info size='mini' className='pending-vra-notes-read-only' data-testid='pending-vra-notes-read-only'>
                <p>You can read the notes. Adding a note needs the Pending VRA editor role.</p>
              </Message>
            )}
          </Form>
        )}

        {validationError && (
          <Message negative size='mini' data-testid='pending-vra-notes-validation'>
            <p>{validationError}</p>
          </Message>
        )}

        {errorMessage && (
          <Message negative data-testid='pending-vra-notes-error'>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={submitting} data-testid='pending-vra-notes-cancel'>
          {PENDING_VRA_NOTE_CANCEL_LABEL}
        </Button>
        {loaded && (
          <Button
            color='green'
            loading={submitting}
            disabled={submitting || !canAddNote}
            data-testid='pending-vra-note-confirm'
            onClick={handleConfirm}
          >
            {PENDING_VRA_NOTE_CONFIRM_LABEL}
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

PendingVraNotesDialog.propTypes = {
  vraId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  dealerName: PropTypes.string,
  notes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    note: PropTypes.string,
    createdBy: PropTypes.string,
    createdAt: PropTypes.string,
  })),
  notesLoading: PropTypes.bool,
  notesError: PropTypes.string,
  submitting: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  canAddNote: PropTypes.bool,
};

PendingVraNotesDialog.defaultProps = {
  dealerName: '',
  notes: [],
  notesLoading: false,
  notesError: null,
  submitting: false,
  errorMessage: null,
  canAddNote: true,
};

export default PendingVraNotesDialog;
